import {
  Activity,
  Brain,
  CalendarDays,
  ClipboardList,
  FolderOpen,
  Lightbulb,
  Mail,
  MessageSquare,
  Monitor,
  Settings,
  type LucideIcon,
} from "lucide-react";

// One name and one icon for each section an agent has, read by Home's agent
// card (features/dashboard/AgentControlView), the Agents page
// (features/agents/workspace/sections) and the jump palette
// (features/navigation/navModel) alike, so the two pages read as one card:
// the Agents page is the card expanded, with the hands-on sections (Chat,
// Desktop, Mail, Tasks) added. A rename here renames it everywhere. No
// React here: the palette's self-tests bundle it for node.

export interface AgentSectionMeta {
  label: string;
  icon: LucideIcon;
}

export const AGENT_SECTION = {
  activity: { label: "Activity", icon: Activity },
  // The agent's day (features/agents/today): what it is doing now, what
  // comes next, its schedules folded under. It was "Schedules"; the key
  // stays "schedule" (Home's view key) and the page segment /routines.
  schedule: { label: "Today", icon: CalendarDays },
  memory: { label: "Memory", icon: Brain },
  files: { label: "Files", icon: FolderOpen },
  skills: { label: "Skills", icon: Lightbulb },
  settings: { label: "Settings", icon: Settings },
} as const satisfies Record<string, AgentSectionMeta>;

export type AgentWorkspaceSection =
  | "chat"
  | "activity"
  | "desktop"
  | "mail"
  | "tasks"
  | "routines"
  | "memory"
  | "files"
  | "skills"
  | "settings";

export interface WorkspaceSectionDef extends AgentSectionMeta {
  key: AgentWorkspaceSection;
}

/**
 * The Agents page's sections in tab order (each a path segment under
 * /agents/<t>/): the card's own (Activity, Today, Memory, Files, Skills,
 * Settings, in the card's order) with the hands-on ones among them.
 * Today keeps its old segment /routines for links.
 */
export const WORKSPACE_SECTIONS: readonly WorkspaceSectionDef[] = [
  { key: "chat", label: "Chat", icon: MessageSquare },
  { key: "activity", ...AGENT_SECTION.activity },
  { key: "desktop", label: "Desktop", icon: Monitor },
  { key: "mail", label: "Mail", icon: Mail },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "routines", ...AGENT_SECTION.schedule },
  { key: "memory", ...AGENT_SECTION.memory },
  { key: "files", ...AGENT_SECTION.files },
  { key: "skills", ...AGENT_SECTION.skills },
  { key: "settings", ...AGENT_SECTION.settings },
];

/** The sections only the Agents page has: the card offers Chat and Desktop as buttons. */
export const HANDS_ON_SECTIONS: ReadonlySet<AgentWorkspaceSection> = new Set(["chat", "desktop", "mail", "tasks"]);

/** Home's agent card's view keys: the page's segment /routines is the card's "schedule". */
export type AgentCardSection = "activity" | "schedule" | "memory" | "files" | "skills" | "settings";

export interface CardSectionDef extends AgentSectionMeta {
  view: AgentCardSection;
}

/**
 * Home's agent card's tabs, read off the same list as the Agents page:
 * WORKSPACE_SECTIONS without the hands-on ones, in the same order, so the
 * card and the page can never disagree on a name, an icon or the order.
 */
export const CARD_SECTIONS: readonly CardSectionDef[] = WORKSPACE_SECTIONS.filter((s) => !HANDS_ON_SECTIONS.has(s.key)).map(
  (s) => ({ view: (s.key === "routines" ? "schedule" : s.key) as AgentCardSection, label: s.label, icon: s.icon }),
);
